import type {
  AdvanceId,
  DisbursementId,
  DrawId,
  FeeId,
  LedgerPostingId,
  PaymentDueId,
  PaymentId,
  PayoffId,
  PrincipalModificationId,
  WorkoutPlanId,
  WriteoffId,
} from '../../BrandedIds';
import type { PaymentDue } from '../../PaymentDue';
import { add, subtract } from '../../utils/math';
import { type Advance, asAdvanceId, type Fee } from '../Charges';
import { type AccountAttributionRequirement, accountAttributionRequirementKeys } from './AccountAttribution';
import { DebitNormalGLAccount, type GLAccount } from './GLAccount';
import type { Posting } from './Posting';
import type { Transaction } from './Transaction';

// Short-named allocator-facing shape (distinct from
// `paymentDueBalances`'s canonical long-named return). DI is tracked
// separately because of distinct remittance / waiver / deferral
// semantics.
export type RemainingReceivables = {
  principal: number;
  interest: number;
  delinquentInterest: number;
  escrow: number;
};

export type RemainingAdvance = { principal: number; interest: number };

// Split combined interest into standard + DI. Mirrors the
// assessment-posting emission shape; used by the no-index fallback.
const splitInterest = (pd: PaymentDue): { standard: number; delinquent: number } => {
  const di = (pd.attribution ?? [])
    .filter((a) => a.type === 'delinquentInterest' && !a.isWaived && !a.isDeferred)
    .reduce((sum, a) => sum + a.interest, 0);
  return { standard: Math.max(pd.interest - di, 0), delinquent: Math.max(di, 0) };
};

// Cached read-side state for a LoanLedger. Maintains in lockstep via
// `recordPosting`: void set, per-account balances, per-(account, id)
// attributed balances, per-account FIFO queues.

const DebitNormalSet = new Set<string>(DebitNormalGLAccount.options);
const isDebitNormal = (account: GLAccount): boolean => DebitNormalSet.has(account);

export type AttributedAccount = keyof AccountAttributionRequirement;
export type AccountBalances = Partial<Record<GLAccount, number>>;

// Recovers attribution fields that `reversalOf`'s type widening
// hides from the narrowed Posting union; they're all present at
// runtime.
type ConcretePosting = {
  id: LedgerPostingId;
  amount: number;
  debitAccount: GLAccount;
  creditAccount: GLAccount;
  voidsPostingId?: LedgerPostingId;
  paymentId?: PaymentId;
  paymentDueId?: PaymentDueId;
  feeId?: FeeId;
  payoffId?: PayoffId;
  disbursementId?: DisbursementId;
  drawId?: DrawId;
  writeoffId?: WriteoffId;
  workoutPlanId?: WorkoutPlanId;
  modificationId?: PrincipalModificationId;
};
const asConcrete = (p: Posting): ConcretePosting => p as unknown as ConcretePosting;

export type AttributionFields = {
  paymentId?: PaymentId;
  paymentDueId?: PaymentDueId;
  feeId?: FeeId;
  payoffId?: PayoffId;
  disbursementId?: DisbursementId;
  drawId?: DrawId;
  writeoffId?: WriteoffId;
  workoutPlanId?: WorkoutPlanId;
  modificationId?: PrincipalModificationId;
};

const ATTRIBUTION_KEYS = [
  'paymentId',
  'paymentDueId',
  'feeId',
  'payoffId',
  'disbursementId',
  'drawId',
  'writeoffId',
  'workoutPlanId',
  'modificationId',
] as const satisfies ReadonlyArray<keyof AttributionFields>;

const extractAttribution = (p: ConcretePosting): AttributionFields => {
  const out: AttributionFields = {};
  for (const key of ATTRIBUTION_KEYS) {
    const v = p[key];
    if (v !== undefined) out[key] = v as never;
  }
  return out;
};

/** A withdrawal drains buckets whose attribution is a SUBSET of the
 *  withdrawal's attribution first; falls back to plain FIFO across
 *  the queue when no match exists. */
const depositMatchesWithdrawal = (deposit: AttributionFields, withdrawal: AttributionFields): boolean => {
  for (const key of ATTRIBUTION_KEYS) {
    const d = deposit[key];
    if (d === undefined) continue;
    if (withdrawal[key] !== d) return false;
  }
  return true;
};

export type AccountAttributionBalance = {
  attribution: AttributionFields;
  remaining: number;
};

export class LedgerIndex {
  private readonly voided = new Set<LedgerPostingId>();
  private readonly balances = new Map<GLAccount, number>();
  private readonly attributed = new Map<AttributedAccount, Map<string, number>>();
  private readonly fifoByAccount = new Map<GLAccount, AccountAttributionBalance[]>();

  constructor(transactions: ReadonlyArray<Transaction> = []) {
    for (const t of transactions) for (const p of t.postings) this.recordPosting(p);
  }

  recordPosting(p: Posting): void {
    const c = asConcrete(p);
    if (c.voidsPostingId) this.voided.add(c.voidsPostingId);

    const debitNormalDebit = isDebitNormal(c.debitAccount);
    const debitNormalCredit = isDebitNormal(c.creditAccount);

    this.bumpAccount(c.debitAccount, debitNormalDebit ? c.amount : -c.amount);
    this.bumpAccount(c.creditAccount, debitNormalCredit ? -c.amount : c.amount);

    this.bumpAttributed(c.debitAccount, debitNormalDebit ? c.amount : -c.amount, c);
    this.bumpAttributed(c.creditAccount, debitNormalCredit ? -c.amount : c.amount, c);

    // A posting can be a deposit on the debit side and a withdrawal
    // on the credit side; skip wash cases.
    if (c.debitAccount !== c.creditAccount) {
      const attr = extractAttribution(c);
      this.applyFifo(c.debitAccount, /* isDebit */ true, c.amount, attr);
      this.applyFifo(c.creditAccount, /* isDebit */ false, c.amount, attr);
    }
  }

  private bumpAccount(account: GLAccount, signed: number): void {
    const cur = this.balances.get(account) ?? 0;
    this.balances.set(account, signed >= 0 ? add(cur, signed) : subtract(cur, -signed));
  }

  private bumpAttributed(account: GLAccount, signed: number, c: ConcretePosting): void {
    const idFields = (accountAttributionRequirementKeys as Record<string, readonly string[] | undefined>)[account];
    if (!idFields) return;
    for (const field of idFields) {
      const idValue = (c as unknown as Record<string, unknown>)[field];
      if (typeof idValue !== 'string') continue;
      let scope = this.attributed.get(account as AttributedAccount);
      if (!scope) {
        scope = new Map();
        this.attributed.set(account as AttributedAccount, scope);
      }
      const cur = scope.get(idValue) ?? 0;
      scope.set(idValue, signed >= 0 ? add(cur, signed) : subtract(cur, -signed));
    }
  }

  private applyFifo(account: GLAccount, isDebit: boolean, amount: number, attribution: AttributionFields): void {
    const isDeposit = isDebit === isDebitNormal(account);
    let queue = this.fifoByAccount.get(account);
    if (!queue) {
      queue = [];
      this.fifoByAccount.set(account, queue);
    }
    if (isDeposit) {
      queue.push({ attribution, remaining: amount });
      return;
    }
    // Withdrawal: matched-first drain, then plain FIFO.
    let toDrain = amount;
    for (const bucket of queue) {
      if (toDrain <= 0) break;
      if (bucket.remaining <= 0) continue;
      if (!depositMatchesWithdrawal(bucket.attribution, attribution)) continue;
      const draw = Math.min(bucket.remaining, toDrain);
      bucket.remaining = subtract(bucket.remaining, draw);
      toDrain = subtract(toDrain, draw);
    }
    if (toDrain > 0) {
      for (const bucket of queue) {
        if (toDrain <= 0) break;
        if (bucket.remaining <= 0) continue;
        const draw = Math.min(bucket.remaining, toDrain);
        bucket.remaining = subtract(bucket.remaining, draw);
        toDrain = subtract(toDrain, draw);
      }
    }
  }

  // ── Read API ──────────────────────────────────────────────────────

  isVoided(id: LedgerPostingId): boolean {
    return this.voided.has(id);
  }

  balance(account: GLAccount): number {
    return this.balances.get(account) ?? 0;
  }

  snapshot(): AccountBalances {
    const out: AccountBalances = {};
    for (const [k, v] of this.balances) out[k] = v;
    return out;
  }

  // Returns `undefined` for untouched (account, id) pairs so callers
  // can distinguish "untouched" from "touched and zero".
  balanceFor(account: AttributedAccount, id: string): number | undefined {
    return this.attributed.get(account)?.get(id);
  }

  fifo(account: GLAccount): AccountAttributionBalance[] {
    const queue = this.fifoByAccount.get(account);
    if (!queue) return [];
    return queue
      .filter((b) => b.remaining > 0)
      .map((b) => ({ attribution: { ...b.attribution }, remaining: b.remaining }));
  }

  // ── Typed convenience wrappers ────────────────────────────────────

  paymentDueBalances(id: PaymentDueId):
    | {
        principalReceivable: number;
        interestReceivable: number;
        delinquentInterestReceivable: number;
        escrowReceivable: number;
      }
    | undefined {
    const touched =
      this.attributed.get('principalReceivable')?.has(id) ||
      this.attributed.get('interestReceivable')?.has(id) ||
      this.attributed.get('escrowReceivable')?.has(id) ||
      this.attributed.get('delinquentInterestReceivable')?.has(id);
    if (!touched) return undefined;
    return {
      principalReceivable: Math.max(this.balanceFor('principalReceivable', id) ?? 0, 0),
      interestReceivable: Math.max(this.balanceFor('interestReceivable', id) ?? 0, 0),
      delinquentInterestReceivable: Math.max(this.balanceFor('delinquentInterestReceivable', id) ?? 0, 0),
      escrowReceivable: Math.max(this.balanceFor('escrowReceivable', id) ?? 0, 0),
    };
  }

  feeReceivable(id: FeeId): number | undefined {
    return this.balanceFor('feeReceivable', id);
  }

  advanceBalances(id: AdvanceId): { advancePrincipal: number; advanceInterest: number } | undefined {
    const p = this.balanceFor('advancePrincipal', id);
    const i = this.balanceFor('advanceInterest', id);
    if (p === undefined && i === undefined) return undefined;
    return {
      advancePrincipal: Math.max(p ?? 0, 0),
      advanceInterest: Math.max(i ?? 0, 0),
    };
  }

  // Allocator views: index-as-truth when available, fall back to the
  // source object's stated amounts when not (no-ledger callers /
  // migration scripts via NULL_INDEX).

  remainingFor(pd: PaymentDue): RemainingReceivables {
    const r = this.paymentDueBalances(pd.id);
    if (r) {
      return {
        principal: r.principalReceivable,
        interest: r.interestReceivable,
        delinquentInterest: r.delinquentInterestReceivable,
        escrow: r.escrowReceivable,
      };
    }
    const interestSplit = splitInterest(pd);
    return {
      principal: pd.principal,
      interest: interestSplit.standard,
      delinquentInterest: interestSplit.delinquent,
      escrow: pd.escrow,
    };
  }

  stillOutstanding(pd: PaymentDue): boolean {
    const known = this.paymentDueBalances(pd.id);
    if (known) {
      return (
        known.principalReceivable > 0 ||
        known.interestReceivable > 0 ||
        known.delinquentInterestReceivable > 0 ||
        known.escrowReceivable > 0
      );
    }
    return !pd.status.fulfilled;
  }

  remainingFeeFor(fee: Fee): number {
    const ledgerRemaining = this.feeReceivable(fee.id);
    if (ledgerRemaining !== undefined) return Math.max(ledgerRemaining, 0);
    return Math.max(fee.amountRemaining, 0);
  }

  remainingAdvanceFor(advance: Advance): RemainingAdvance {
    const ledgerRemaining = this.advanceBalances(asAdvanceId(advance));
    if (ledgerRemaining) {
      return { principal: ledgerRemaining.advancePrincipal, interest: ledgerRemaining.advanceInterest };
    }
    return {
      principal: Math.max(advance.advancePrincipal, 0),
      interest: Math.max(advance.advanceInterest, 0),
    };
  }

  suspenseSources(): ReadonlyArray<{ paymentId: PaymentId; remaining: number }> {
    return this.fifo('suspense')
      .filter((b): b is AccountAttributionBalance & { attribution: { paymentId: PaymentId } } =>
        Boolean(b.attribution.paymentId),
      )
      .map((b) => ({ paymentId: b.attribution.paymentId, remaining: b.remaining }));
  }

  static balancesByPosting(transactions: ReadonlyArray<Transaction>): Map<LedgerPostingId, AccountBalances> {
    const out = new Map<LedgerPostingId, AccountBalances>();
    const idx = new LedgerIndex();
    for (const txn of transactions) {
      for (const posting of txn.postings) {
        idx.recordPosting(posting);
        out.set(asConcrete(posting).id, idx.snapshot());
      }
    }
    return out;
  }

  // Net per-account delta from a posting set ("what did THESE
  // postings move?"). Order-independent.
  static deltaOf(postings: ReadonlyArray<Posting>): AccountBalances {
    const idx = new LedgerIndex();
    for (const p of postings) idx.recordPosting(p);
    return idx.snapshot();
  }
}

// Singleton empty index used by allocator paths that don't have a
// live LoanLedger to share with — every read returns 0 / undefined.
export const NULL_INDEX = new LedgerIndex();
